import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { unsubscribeService } from '../config/unsubscribeApi';

const UnsubscribePage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitResult(null);

    if (!email.trim()) {
      setSubmitResult({
        type: 'error',
        message: 'Please enter your email address'
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setSubmitResult({
        type: 'error',
        message: 'Please enter a valid email address'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await unsubscribeService.submitUnsubscribe(email);
      
      setSubmitResult({
        type: 'success',
        message: response.message || 'You have been successfully unsubscribed from our mailing list.'
      });
      setEmail('');
    } catch (error) {
      setSubmitResult({
        type: 'error',
        message: error.message || 'Failed to process your request. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F7F9FC] min-h-screen">
      {/* Header */}
      <section className="py-24 px-6 md:px-12 bg-[#202356]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-3xl md:text-5xl lg:text-4xl font-semibold text-white mb-4">
              Unsubscribe
            </h1>
            <p className="text-lg text-white opacity-90">
              We're sorry to see you go. Enter your email address below to unsubscribe from our mailing list.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-2xl mx-auto">
          <motion.div
            className="bg-white rounded-lg shadow-lg p-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Status Messages */}
            {submitResult && (
              <div className={`mb-6 p-4 rounded-lg ${
                submitResult.type === 'success' 
                  ? 'bg-green-100 border border-green-400 text-green-700' 
                  : 'bg-red-100 border border-red-400 text-red-700'
              }`}>
                <p className="font-medium">{submitResult.message}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#202356] focus:border-transparent transition-all"
                  placeholder="Enter your email address"
                  required
                  disabled={isSubmitting}
                />
                <p className="mt-2 text-sm text-gray-500">
                  Enter the email address you wish to unsubscribe from our communications.
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#202356] hover:bg-[#2d3270] disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-4 rounded-xl font-medium transition-colors"
              >
                {isSubmitting ? 'Processing...' : 'Unsubscribe'}
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600 text-center">
                Changed your mind? You can resubscribe at any time by contacting our support team.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default UnsubscribePage;
